import React, { useState } from 'react'
import { Alert, Box, Button, Collapse, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ThemeEditor from '../theme-ai/ThemeEditor'
import type { AutoCoderCode } from '../../types/gotypes'

const REFINEMENT_NUDGE_THRESHOLD = 5

interface ProposedThemesTrayProps {
  proposedThemes: AutoCoderCode[]
  onThemesChange: (themes: AutoCoderCode[]) => void
  /** The saved frame's theme names; a proposal that leaves some out says so before it is saved. */
  frameThemeNames: string[]
  /** The task already has a frame, so saving makes a new version of it. */
  hasFrame: boolean
  nextVersion: number
  saving: boolean
  loading: boolean
  refinements: number
  sampleSize: number
  sampleSizes: readonly number[]
  poolSize: number
  onSampleSizeChange: (size: number) => void
  onSave: () => Promise<void>
}

// What the agent has proposed, sitting above the composer in the side pane:
// a one-line bar with the save action, opening into the full editor. The chat
// keeps the pane; the themes are one click away.
const ProposedThemesTray: React.FC<ProposedThemesTrayProps> = ({
  proposedThemes,
  onThemesChange,
  frameThemeNames,
  hasFrame,
  nextVersion,
  saving,
  loading,
  refinements,
  sampleSize,
  sampleSizes,
  poolSize,
  onSampleSizeChange,
  onSave,
}) => {
  const [expanded, setExpanded] = useState(true)
  const count = proposedThemes.length
  // The agent sometimes answers a question ("what is missing?") with only the
  // new themes; saving that would silently drop the rest of the frame.
  const proposedNames = new Set(proposedThemes.map((theme) => (theme.text || theme.code).trim().toLowerCase()))
  const dropped = count > 0 ? frameThemeNames.filter((name) => !proposedNames.has(name.trim().toLowerCase())) : []
  const sizes = sampleSizes.filter((size) => size < poolSize)

  const handleSave = async () => {
    try {
      await onSave()
    } catch {
      // Reported by the hook; the proposal stays for another try.
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">The agent reads</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={Math.min(sampleSize, poolSize)}
          onChange={(_, value) => value && onSampleSizeChange(value)}
          aria-label="How many responses the agent reads"
          disabled={loading}
        >
          {sizes.map((size) => (
            <ToggleButton key={size} value={size} sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>{size}</ToggleButton>
          ))}
          {poolSize <= sampleSizes[sampleSizes.length - 1] && (
            <ToggleButton value={poolSize} sx={{ px: 1, py: 0.25, fontSize: '0.75rem' }}>All {poolSize}</ToggleButton>
          )}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">responses</Typography>
      </Box>

      {count > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
            <Button
              color="info"
              size="small"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease-out' }} />}
              sx={{ ml: -0.5 }}
            >
              {count} proposed theme{count === 1 ? '' : 's'}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" color="info" size="small" disabled={saving || loading} onClick={handleSave}>
              {saving ? 'Saving…' : hasFrame ? `Save as version ${nextVersion}` : `Use these ${count} themes`}
            </Button>
          </Box>
          {dropped.length > 0 && (
            <Alert severity="warning" sx={{ mx: 1.5, mb: 1 }}>
              {dropped.length} of the saved themes are not in this proposal by name ({dropped.slice(0, 3).join(', ')}
              {dropped.length > 3 ? ', …' : ''}). If that is not a rename you meant, ask the agent to keep them before saving.
            </Alert>
          )}
          <Collapse in={expanded}>
            <Box sx={{ maxHeight: '42vh', overflowY: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
              {refinements >= REFINEMENT_NUDGE_THRESHOLD && (
                <Alert severity="info" sx={{ m: 1.5, mb: 0 }}>
                  You have refined the themes {refinements} times. A test on a sample is often the quickest way to see what still needs changing.
                </Alert>
              )}
              <ThemeEditor
                themes={proposedThemes}
                isLoading={false}
                onAddTheme={(theme) => onThemesChange([...proposedThemes, theme])}
                onUpdateTheme={(previous, theme) =>
                  onThemesChange(proposedThemes.map((existing) => (existing.code === previous ? theme : existing)))
                }
                onDeleteTheme={(code) => onThemesChange(proposedThemes.filter((theme) => theme.code !== code))}
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  )
}

export default ProposedThemesTray
